/* Homepage hero slideshow (prompt 84): autoplay every 6s, pause on hover / focus / touch (and the pause button),
   swipe on phones, arrows on desktop, dots, arrow keys. prefers-reduced-motion: no autoplay, no slide animation.
   Keeps --hs-s (stage scale) in step with the hero's width. Off-screen slides are inert (no tabbing into them). */
(() => {
  const root = document.querySelector('[data-ssc-hs]');
  const viewport = root?.querySelector('[data-ssc-hs-viewport]');
  if (!root || !viewport) return;
  const track = root.querySelector('[data-ssc-hs-track]');
  const slides = [...root.querySelectorAll('[data-ssc-hs-slide]')];
  const dots = [...root.querySelectorAll('[data-ssc-hs-dot]')];
  const pauseBtn = root.querySelector('[data-ssc-hs-pause]');
  const live = root.querySelector('[data-ssc-hs-live]');
  const desktop = window.matchMedia('(min-width: 990px)');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DELAY = 6000;

  const scale = () => {
    const w = viewport.clientWidth || window.innerWidth;
    viewport.style.setProperty('--hs-s', String(desktop.matches ? Math.min(0.8, (w - 48) / 1800) : w / 1080));
  };
  scale();
  new ResizeObserver(scale).observe(viewport);
  desktop.addEventListener('change', scale);

  if (slides.length < 2) return;
  let index = 0;
  let timer = null;
  let hovered = false;
  let focused = false;
  let stopped = still.matches; // pause button, a touch, or reduced motion

  const show = (i, announce) => {
    index = (i + slides.length) % slides.length;
    track.style.transform = `translateX(${-100 * index}%)`;
    slides.forEach((s, n) => { s.inert = n !== index; });
    dots.forEach((d, n) => (n === index ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
    if (announce && live) live.textContent = `Slide ${index + 1} of ${slides.length}`;
  };
  const tick = () => {
    clearTimeout(timer);
    timer = null;
    if (stopped || hovered || focused || document.hidden) return;
    timer = setTimeout(() => { show(index + 1, false); tick(); }, DELAY);
  };
  const stop = () => {
    stopped = true;
    pauseBtn?.setAttribute('aria-pressed', 'true');
    pauseBtn?.setAttribute('aria-label', 'Play the slideshow');
    tick();
  };
  const go = (i) => { show(i, true); tick(); };

  root.querySelector('[data-ssc-hs-prev]')?.addEventListener('click', () => go(index - 1));
  root.querySelector('[data-ssc-hs-next]')?.addEventListener('click', () => go(index + 1));
  dots.forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.sscHsDot))));
  pauseBtn?.addEventListener('click', () => {
    if (stopped) {
      stopped = false;
      pauseBtn.setAttribute('aria-pressed', 'false');
      pauseBtn.setAttribute('aria-label', 'Pause the slideshow');
      tick();
    } else {
      stop();
    }
  });
  if (still.matches) {
    pauseBtn?.setAttribute('aria-pressed', 'true');
    pauseBtn?.setAttribute('aria-label', 'Play the slideshow');
  }

  root.addEventListener('mouseenter', () => { hovered = true; tick(); });
  root.addEventListener('mouseleave', () => { hovered = false; tick(); });
  root.addEventListener('focusin', () => { focused = true; tick(); });
  root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { focused = false; tick(); } });
  document.addEventListener('visibilitychange', tick);
  root.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
  });

  // Swipe: horizontal drags change slide; vertical ones scroll the page (touch-action: pan-y).
  let x0 = null;
  let y0 = 0;
  let swiped = false;
  viewport.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch') stop(); // a touch means the visitor is in control
    x0 = e.clientX;
    y0 = e.clientY;
    swiped = false;
  });
  viewport.addEventListener('pointerup', (e) => {
    if (x0 === null) return;
    const dx = e.clientX - x0;
    const dy = e.clientY - y0;
    x0 = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      swiped = true;
      go(index + (dx < 0 ? 1 : -1));
    }
  });
  viewport.addEventListener('pointercancel', () => { x0 = null; });
  // A drag that started on a button link shouldn't also follow the link.
  viewport.addEventListener('click', (e) => {
    if (swiped) { e.preventDefault(); swiped = false; }
  }, true);

  show(0, false);
  tick();
})();
